import { IBookmark } from '@/common/types/IBookmark';
import {useEffect, useState, useRef} from "react";
import {folderService} from "@/utilities/folder/FolderService.ts";
import { useBookmarks } from '@/hooks/useBookmarks';
import { useFolders } from '@/hooks/useFolders';
import FolderSelector from '../folder/FolderSelector';

interface MobileBookmarkListProps {
  bookmarks: IBookmark[];
  loading?: boolean;
  onUpdate: (bookmark: IBookmark) => Promise<void>;
  onDelete?: (id: string) => Promise<void>;
  onRefresh?: () => Promise<void>;
  layout?: 'single' | 'multi-column';
}

export default function MobileBookmarkList({
  bookmarks,
  loading,
  onUpdate,
  onDelete,
  onRefresh,
  layout = 'single',
}: MobileBookmarkListProps) {
  const [folderMap, setFolderMap] = useState<any>({});
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [showFolderSelector, setShowFolderSelector] = useState(false);
  const [selectedBookmarkForFolder, setSelectedBookmarkForFolder] = useState<IBookmark | null>(null);
  const menuRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const { deleteBookmark: deleteBookmarkFromHook, refreshBookmarks: refreshBookmarksFromHook } = useBookmarks();
  const { moveBookmarkToFolder } = useFolders();

  // Use props if provided, otherwise fall back to hook
  const deleteBookmark = onDelete || deleteBookmarkFromHook;
  const refreshBookmarks = onRefresh || refreshBookmarksFromHook;

  useEffect(() => {
    folderService.getAll(false).then(folders => {
        const map: {[key: string]: string} = {};
        folders.forEach(folder => {
            map[folder.id] = folder.name;
        });
        setFolderMap(map);
    })
  }, [])

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openMenuId && menuRefs.current[openMenuId]) {
        const menuElement = menuRefs.current[openMenuId];
        if (menuElement && !menuElement.contains(event.target as Node)) {
          setOpenMenuId(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openMenuId]);
  const formatTimeAgo = (timestamp: number) => {
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes} mins ago`;
    if (hours < 24) return `${hours} hours ago`;
    if (days === 1) return 'yesterday';
    if (days < 7) return `${days} days ago`;
    return new Date(timestamp).toLocaleDateString();
  };

  const extractDomain = (url: string | undefined) => {
    if (!url) return 'UNKNOWN';
    try {
      return new URL(url).hostname.replace('www.', '').toUpperCase();
    } catch {
      return 'UNKNOWN';
    }
  };

  const handleMenuToggle = (bookmarkId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setOpenMenuId(openMenuId === bookmarkId ? null : bookmarkId);
  };

  const handleDeleteBookmark = async (bookmark: IBookmark) => {
    if (window.confirm('Are you sure you want to delete this bookmark?')) {
      await deleteBookmark(bookmark.id);
      setOpenMenuId(null);
      await refreshBookmarks();
    }
  };

  const handleCopyLink = async (bookmark: IBookmark) => {
    if (bookmark.url) {
      try {
        await navigator.clipboard.writeText(bookmark.url);
        setOpenMenuId(null);
        // You could add a toast notification here
      } catch (err) {
        console.error('Failed to copy link:', err);
      }
    }
  };

  const handleChangeFolder = (bookmark: IBookmark) => {
    setSelectedBookmarkForFolder(bookmark);
    setShowFolderSelector(true);
    setOpenMenuId(null);
  };

  const handleFolderSelect = async (folderId?: string) => {
    if (selectedBookmarkForFolder) {
      await moveBookmarkToFolder(selectedBookmarkForFolder.id, folderId);
      await refreshBookmarks();
      setShowFolderSelector(false);
      setSelectedBookmarkForFolder(null);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
        <p className="ml-3 text-gray-600">Loading bookmarks...</p>
      </div>
    );
  }

  if (bookmarks.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
          <span className="text-2xl">📚</span>
        </div>
        <p className="text-gray-600">No bookmarks yet</p>
        <p className="text-sm text-gray-500 mt-1">Start saving your favorite content</p>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Bookmarks List */}
      <div className={layout === 'single' ? 'space-y-4' : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4'}>
        {bookmarks.map((bookmark) => (
          <div
            key={bookmark.id}
            className={`bg-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow ${
              layout === 'single' ? 'p-4' : 'p-3'
            }`}
          >
            {layout === 'single' ? (
              // Single column layout (original mobile design)
              <div className="flex items-start space-x-3">
                {/* Thumbnail */}
                <div className="flex-shrink-0">
                  {bookmark.image ? (
                    <img
                      src={bookmark.image}
                      alt={bookmark.title}
                      className="w-16 h-16 object-cover rounded"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        target.nextElementSibling?.classList.remove('hidden');
                      }}
                    />
                  ) : null}
                  <div className={`w-16 h-16 bg-gray-100 rounded flex items-center justify-center ${bookmark.image ? 'hidden' : ''}`}>
                    <span className="text-lg">🔖</span>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  {/* Source and Time */}
                  <div className="flex items-center text-xs text-gray-500 mb-1">
                    <span className="font-medium">{extractDomain(bookmark.url)}</span>
                    <span className="mx-1">•</span>
                    <span>{formatTimeAgo(bookmark.addedOn)}</span>
                    <span className="mx-1">•</span>
                    <div className={'text-white pt-1 pb-1 pr-2 pl-2 bg-primary-dark-main rounded-2xl'}>{folderMap[bookmark.folderId || ''] || 'Uncategorized'}</div>

                  </div>

                  {/* Title */}
                  <h3 className="font-semibold text-gray-900 mb-1 line-clamp-2">
                    <a
                      href={bookmark.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-purple-600"
                    >
                      {bookmark.title || bookmark.url}
                    </a>
                  </h3>

                  {/* Description */}
                  {bookmark.description && (
                    <p className="text-sm text-gray-600 line-clamp-2 mb-2">
                      {bookmark.description}
                    </p>
                  )}

                  {/* Tags */}
                  {bookmark.tags && bookmark.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {bookmark.tags.slice(0, 3).map((tag) => (
                        <span
                          key={tag.name}
                          className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-full"
                        >
                          {tag.name}
                        </span>
                      ))}
                      {bookmark.tags.length > 3 && (
                        <span className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-full">
                          +{bookmark.tags.length - 3}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex-shrink-0 flex items-center gap-1">
                  <button
                    onClick={() => onUpdate({ ...bookmark, is_starred: bookmark.is_starred === 'true' ? 'false' : 'true' })}
                    className="p-1 text-gray-400 hover:text-yellow-500 transition-colors"
                  >
                    {bookmark.is_starred === 'true' ? '⭐' : '☆'}
                  </button>
                  <div className="relative" ref={(el) => (menuRefs.current[bookmark.id] = el)}>
                    <button
                      onClick={(e) => handleMenuToggle(bookmark.id, e)}
                      className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                      </svg>
                    </button>
                    {openMenuId === bookmark.id && (
                      <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
                        <div className="py-1">
                          <button
                            onClick={() => handleDeleteBookmark(bookmark)}
                            className="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            Delete bookmark
                          </button>
                          <button
                            onClick={() => handleCopyLink(bookmark)}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                            Copy link
                          </button>
                          <button
                            onClick={() => handleChangeFolder(bookmark)}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                            </svg>
                            Change folder
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              // Multi-column layout (compact cards)
              <div className="space-y-3">
                {/* Thumbnail */}
                <div className="aspect-video bg-gray-100 rounded overflow-hidden relative">
                  <div className="absolute top-1 right-1 z-10 flex items-center gap-1">
                    <button
                      onClick={() => onUpdate({ ...bookmark, is_starred: bookmark.is_starred === 'true' ? 'false' : 'true' })}
                      className="pr-2 pl-2 pt-1 pb-1 text-yellow-500 hover:text-yellow-500 transition-colors bg-white/80 dark:bg-gray-200/80 rounded"
                    >
                      {bookmark.is_starred === 'true' ? '⭐' : '☆'}
                    </button>
                  </div>
                  {bookmark.image ? (
                    <img
                      src={bookmark.image}
                      alt={bookmark.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        target.nextElementSibling?.classList.remove('hidden');
                      }}
                    />
                  ) : null}
                  <div className={`w-full h-full flex items-center justify-center ${bookmark.image ? 'hidden' : ''}`}>
                    <span className="text-2xl">🔖</span>
                  </div>
                </div>

                {/* Content */}
                <div className="space-y-2">
                  {/* Source and Time */}
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span className="font-medium">{extractDomain(bookmark.url)}</span>
                    <div className={'text-white pt-1 pb-1 pr-2 pl-2 bg-primary-dark-main rounded-2xl'}>{folderMap[bookmark.folderId || ''] || 'Uncategorized'}</div>
                  </div>

                  {/* Title */}
                  <h3 className="font-semibold text-gray-900 line-clamp-2 text-sm">
                    <a
                      href={bookmark.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-purple-600"
                    >
                      {bookmark.title || bookmark.url}
                    </a>
                  </h3>

                  {/* Description */}
                  {bookmark.description && (
                    <p className="text-xs text-gray-600 line-clamp-2">
                      {bookmark.description}
                    </p>
                  )}

                  {/* Tags and Actions */}
                  <div className="flex items-center justify-between">
                    {bookmark.tags && bookmark.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {bookmark.tags.slice(0, 2).map((tag) => (
                          <span
                            key={tag.name}
                            className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-700 rounded-full"
                          >
                            {tag.name}
                          </span>
                        ))}
                        {bookmark.tags.length > 2 && (
                          <span className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-700 rounded-full">
                            +{bookmark.tags.length - 2}
                          </span>
                        )}
                      </div>
                    )}
                    <span className="text-xs text-gray-500">{formatTimeAgo(bookmark.addedOn)}</span>
                    <div className="relative" ref={(el) => (menuRefs.current[bookmark.id] = el)}>
                      <button
                        onClick={(e) => handleMenuToggle(bookmark.id, e)}
                        className="p-1 text-gray-400 hover:text-gray-600 transition-colors rounded"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                        </svg>
                      </button>
                      {openMenuId === bookmark.id && (
                        <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
                          <div className="py-1">
                            <button
                              onClick={() => handleDeleteBookmark(bookmark)}
                              className="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                              Delete bookmark
                            </button>
                            <button
                              onClick={() => handleCopyLink(bookmark)}
                              className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                              Copy link
                            </button>
                            <button
                              onClick={() => handleChangeFolder(bookmark)}
                              className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                              </svg>
                              Change folder
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      <FolderSelector
        visible={showFolderSelector}
        onClose={() => {
          setShowFolderSelector(false);
          setSelectedBookmarkForFolder(null);
        }}
        onSelect={handleFolderSelect}
        currentFolderId={selectedBookmarkForFolder?.folderId}
        title="Change Folder"
      />
    </div>
  );
}
